// Documents folders: the tree, who can see each one, and moving files between them.
// Owners and managers manage folders; locked folders are hidden from everyone else by the database.
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';

export interface DocFolder {
  id: string;
  parent_id: string | null;
  name: string;
  restricted: boolean;
  system_key: string | null;
  position: number;
}
export interface FolderAccess { folder_id: string; role: string | null; user_id: string | null }

const MIGRATION = 'Folders need the latest database update (20261028040000_document_folders.sql).';
const friendly = (m: string) => (/(relation|column) "?[\w.]*(document_folder|folder_id)[\w.]*"? does not exist|schema cache|function public\.ensure_default_folders/i.test(m) ? MIGRATION : m);

export function useDocFolders(venueId: string, canManage: boolean) {
  const [folders, setFolders] = useState<DocFolder[]>([]);
  const [access, setAccess] = useState<FolderAccess[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await supabase.from('document_folders').select('id, parent_id, name, restricted, system_key, position').eq('venue_id', venueId).order('position').order('name');
    if (r.error) { setError(friendly(r.error.message)); setReady(true); return; }
    let list = (r.data ?? []) as DocFolder[];
    // First visit by a manager: make the default folders (and put existing files in them).
    if (list.length === 0 && canManage) {
      await supabase.rpc('ensure_default_folders', { p_venue: venueId });
      const again = await supabase.from('document_folders').select('id, parent_id, name, restricted, system_key, position').eq('venue_id', venueId).order('position').order('name');
      list = (again.data ?? []) as DocFolder[];
    }
    setFolders(list);
    const a = await supabase.from('document_folder_access').select('folder_id, role, user_id').in('folder_id', list.map((f) => f.id));
    setAccess((a.data ?? []) as FolderAccess[]);
    setError(null);
    setReady(true);
  }, [venueId, canManage]);
  useEffect(() => { void load(); }, [load]);

  const create = useCallback(async (name: string, parentId: string | null) => {
    const { data, error: e } = await supabase.from('document_folders')
      .insert({ venue_id: venueId, name: name.trim(), parent_id: parentId, position: 50 }).select('id').single();
    if (e) return { error: friendly(e.message), id: null };
    await load();
    return { error: null, id: (data as { id: string }).id };
  }, [venueId, load]);

  const rename = useCallback(async (id: string, name: string) => {
    const { error: e } = await supabase.from('document_folders').update({ name: name.trim(), updated_at: new Date().toISOString() }).eq('id', id);
    if (!e) await load();
    return { error: e ? friendly(e.message) : null };
  }, [load]);

  const remove = useCallback(async (id: string) => {
    const { error: e } = await supabase.from('document_folders').delete().eq('id', id);
    if (!e) await load();
    return { error: e ? friendly(e.message) : null };
  }, [load]);

  const move = useCallback(async (id: string, parentId: string | null) => {
    // Never into itself or one of its own subfolders.
    let p = parentId;
    for (let i = 0; p && i < 30; i++) { if (p === id) return { error: "A folder can't go inside itself." }; p = folders.find((f) => f.id === p)?.parent_id ?? null; }
    const { error: e } = await supabase.from('document_folders').update({ parent_id: parentId, updated_at: new Date().toISOString() }).eq('id', id);
    if (!e) await load();
    return { error: e ? friendly(e.message) : null };
  }, [folders, load]);

  /** Who can see a folder: everyone, or only these teams and people (owners and managers always can). */
  const setFolderAccess = useCallback(async (id: string, restricted: boolean, roles: string[], userIds: string[]) => {
    const { error: e1 } = await supabase.from('document_folders').update({ restricted, updated_at: new Date().toISOString() }).eq('id', id);
    if (e1) return { error: friendly(e1.message) };
    const { error: e2 } = await supabase.from('document_folder_access').delete().eq('folder_id', id);
    if (e2) return { error: friendly(e2.message) };
    const rows = restricted ? [...roles.map((role) => ({ folder_id: id, role })), ...userIds.map((user_id) => ({ folder_id: id, user_id }))] : [];
    if (rows.length) {
      const { error: e3 } = await supabase.from('document_folder_access').insert(rows);
      if (e3) return { error: friendly(e3.message) };
    }
    await load();
    return { error: null };
  }, [load]);

  /** Put a file in a folder (null = not in a folder). */
  const moveDoc = useCallback(async (bucket: string, path: string, folderId: string | null, docType: string | null) => {
    const { error: e } = await supabase.from('document_meta').upsert(
      { venue_id: venueId, bucket, path, doc_type: docType ?? 'other', folder_id: folderId, updated_at: new Date().toISOString() },
      { onConflict: 'bucket,path' });
    return { error: e ? friendly(e.message) : null };
  }, [venueId]);

  const pathOf = useCallback((id: string | null): DocFolder[] => {
    const out: DocFolder[] = [];
    let cur = id ? folders.find((f) => f.id === id) : undefined;
    for (let i = 0; cur && i < 30; i++) { out.unshift(cur); cur = cur.parent_id ? folders.find((f) => f.id === cur!.parent_id) : undefined; }
    return out;
  }, [folders]);

  /** Locked by this folder or one above it. */
  const isLocked = useCallback((id: string | null) => pathOf(id).some((f) => f.restricted), [pathOf]);

  return { folders, access, ready, error, reload: load, create, rename, remove, move, setFolderAccess, moveDoc, pathOf, isLocked };
}
